/**
 * An asset's masters (1.2.0): the list and its actions, against /api/v1/assets/:id/masters (key:
 * queryKeys.assetMasters(id); a change also refreshes the asset, whose detail carries `masters`, and the Library,
 * whose badges and filters follow them). Owned by the masters UI stream (M2) from step 0 on; "Add master…" (the
 * resumable upload into the current version, POST /api/v1/assets/:id/masters/uploads with AddMasterBody, then the
 * usual chunk and complete routes) is added here by that stream.
 */
import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { MasterDTO, PreviousVersionMasters } from '@prism/contracts';
import { api } from '../client';
import { queryKeys } from '../queryKeys';
import { uploader, useUploads, type MasterTarget, type UploadItem } from '../../upload/uploader';

export function useAssetMasters(assetId: string | null | undefined) {
  return useQuery({
    queryKey: queryKeys.assetMasters(assetId),
    // `previous` (1.2.2): the variants the version before had and the current one has not.
    queryFn: () => api<{ items: MasterDTO[]; previous?: PreviousVersionMasters | null }>(`/api/v1/assets/${assetId}/masters`),
    enabled: Boolean(assetId),
  });
}

/** Edit variant (409 MASTER_EXISTS when the asset has that variant and format already) and remove (never the primary). */
export function useMasterMutations(assetId: string) {
  const qc = useQueryClient();
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: queryKeys.assetMasters(assetId) });
    void qc.invalidateQueries({ queryKey: queryKeys.asset(assetId) });
    void qc.invalidateQueries({ queryKey: queryKeys.assets() });
  };
  return {
    setVariant: useMutation({
      mutationFn: ({ masterId, variantId }: { masterId: string; variantId: string }) =>
        api<MasterDTO>(`/api/v1/assets/${assetId}/masters/${masterId}`, { method: 'PATCH', json: { variantId } }),
      onSuccess: refresh,
    }),
    remove: useMutation({
      mutationFn: (masterId: string) => api<{ removed: boolean }>(`/api/v1/assets/${assetId}/masters/${masterId}`, { method: 'DELETE' }),
      onSuccess: refresh,
    }),
  };
}

/**
 * "Add master…": the file goes up as a resumable upload into the asset's current version (POST
 * /api/v1/assets/:id/masters/uploads, then the usual chunks and complete; the tray shows it, and asks to replace on
 * MASTER_EXISTS). Returns the asset's master uploads still on their way, and refreshes the list when one finishes.
 */
export function useAddMaster(assetId: string) {
  const qc = useQueryClient();
  const uploads = useUploads().filter((u) => u.master?.assetId === assetId);
  const done = uploads.filter((u) => u.state === 'done').length;
  const seen = useRef(done);
  useEffect(() => {
    if (done === seen.current) return;
    seen.current = done;
    void qc.invalidateQueries({ queryKey: queryKeys.assetMasters(assetId) });
    void qc.invalidateQueries({ queryKey: queryKeys.asset(assetId) });
  }, [done, assetId, qc]);
  return {
    add: (file: File, target: Omit<MasterTarget, 'assetId'>) => uploader.add([file], { master: { ...target, assetId } }),
    inFlight: uploads.filter((u): u is UploadItem => !['done', 'cancelled'].includes(u.state)),
  };
}
